import styled, { css } from 'styled-components';

type ButtonProps = {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger' | 'clear';
  size?: 'regular' | 'small';
  disabled?: boolean;
};

type StyleMap = Record<Required<ButtonProps>['variant'], ReturnType<typeof css>>;

const primaryStyles = css<ButtonProps>`
  ${({ theme: { shadows } }) => css`
    box-shadow: ${shadows.s};
    -webkit-box-shadow: ${shadows.s};
    -moz-box-shadow: ${shadows.s};
  `}

  background: ${(props) => props.theme.semanticColors.buttons.primary};
  color: ${(props) => props.theme.colors.white};

  &:hover {
    background: ${(props) => props.theme.colors.green[500]};
  }

  &:active {
    background: ${(props) => props.theme.colors.green[700]};
  }

  &:focus {
    background: ${(props) => props.theme.colors.green[400]};
    &::after {
      border-color: ${(props) => props.theme.colors.blue[300]};
    }
  }

  opacity: ${(props) => (props.disabled ? '40%' : '100%')};
`;

const secondaryStyles = css<ButtonProps>`
  background: transparent;
  color: ${(props) => props.theme.colors.gray[300]};
  border-color: ${(props) => props.theme.colors.gray[300]};

  &:hover {
    background: ${(props) => props.theme.colors.gray[50]};
  }

  &:active {
    background: ${(props) => props.theme.colors.gray[200]};
  }

  &:focus {
    &::after {
      border-color: ${(props) => props.theme.colors.blue[300]};
    }
  }

  opacity: ${(props) => (props.disabled ? '40%' : '100%')};
`;

const tertiaryStyles = css<ButtonProps>`
  background: transparent;
  color: ${(props) => props.theme.colors.gray[300]};
  border-color: transparent;

  &:hover {
    background: ${(props) => props.theme.colors.gray[50]};
  }

  &:active {
    background: ${(props) => props.theme.colors.gray[200]};
  }

  &:focus {
    &::after {
      border-color: ${(props) => props.theme.colors.blue[300]};
    }
  }

  opacity: ${(props) => (props.disabled ? '40%' : '100%')};
`;

const clearStyles = css<ButtonProps>`
  ${({ theme: { shadows } }) => css`
    box-shadow: ${shadows.s};
    -webkit-box-shadow: ${shadows.s};
    -moz-box-shadow: ${shadows.s};
  `}

  background: ${(props) => props.theme.colors.gray[100]};
  color: ${(props) => props.theme.colors.gray[300]};

  &:hover {
    background: ${(props) => props.theme.colors.gray[200]};
  }

  &:active {
    background: ${(props) => props.theme.colors.gray[400]};
    color: ${(props) => props.theme.colors.gray[100]};
  }

  &:focus {
    &::after {
      border-color: ${(props) => props.theme.colors.blue[300]};
    }
  }

  opacity: ${(props) => (props.disabled ? '40%' : '100%')};
`;

const dangerStyles = css<ButtonProps>`
  ${({ theme: { shadows } }) => css`
    box-shadow: ${shadows.s};
    -webkit-box-shadow: ${shadows.s};
    -moz-box-shadow: ${shadows.s};
  `}

  background: ${(props) => props.theme.semanticColors.error};
  color: ${(props) => props.theme.colors.white};

  &:hover {
    background: ${(props) => props.theme.colors.red[400]};
  }

  &:active {
    background: ${(props) => props.theme.colors.red[600]};
  }

  &:focus {
    border-color: ${(props) => props.theme.colors.blue[300]};
    &::after {
      border-color: ${(props) => props.theme.colors.blue[300]};
    }
  }

  opacity: ${(props) => (props.disabled ? '40%' : '100%')};
`;

const styleMap: StyleMap = {
  primary: primaryStyles,
  secondary: secondaryStyles,
  tertiary: tertiaryStyles,
  danger: dangerStyles,
  clear: clearStyles,
};

const Button = styled.button.attrs(() => ({
  type: 'button' as const,
}))<ButtonProps>`
  border: 1px solid transparent;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: ${({ size = 'regular', theme }) =>
    `${theme.spacing.pxToRem(size === 'regular' ? 12 : 7)} ${theme.spacing[16]}`};
  border-radius: ${(props) => props.theme.borderRadius[4]};
  font-size: ${({ size = 'regular', theme }) =>
    size === 'regular'
      ? theme.typography.body1.regular.fontSize
      : theme.typography.body2.regular.fontSize};
  line-height: ${({ size = 'regular', theme }) =>
    size === 'regular'
      ? theme.typography.body1.regular.fontSize
      : theme.typography.body2.regular.fontSize};
  font-weight: ${(props) => props.theme.typography.body1.regular.fontWeight};
  transition: all 0.2s ease-in-out;
  position: relative;

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    pointer-events: none;
  }

  &::after {
    transition: all 0.2s ease-in-out;
    content: '';
    position: absolute;
    width: calc(100% + 4px);
    height: calc(100% + 4px);
    top: -2px;
    left: -2px;
    border: 2px solid transparent;
    border-radius: ${(props) => props.theme.borderRadius[4]};
  }

  ${({ variant = 'primary' }) => styleMap[variant] ?? styleMap.primary}
`;

export default Button;
